// filepath: apps/web/src/admin/tenants/DeprovisionDialog.tsx
//
// FR-TEN-6 [M] / FR-UI-ADM-15 [M] / ISS-050: irreversibility confirmation dialog.
//
// The SPA presents a typed-slug gate (FR-UI-ADM-15 + AC-9): the user
// must type the exact tenant slug to enable the "Confirm
// De-provision" button. The dialog also surfaces a clear warning that
// the operation is permanent, lists what will be deleted, and shows
// the export path (the data written by the saga's export_data step).
//
// SECURITY (INV-9): all text rendering uses React text nodes. No
// `dangerouslySetInnerHTML`. Tenant-authored text is never rendered
// as raw HTML in this dialog.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { focusTrap } from "@/runtime/focusTrap";

export interface DeprovisionDialogProps {
  /** Tenant slug to be deleted — used for the typed-slug gate. */
  tenantSlug: string;
  /** Tenant display name (optional, used in the dialog title). */
  tenantName?: string;
  /** Path the export data will be written to (informational). */
  exportPath?: string;
  /** True while the start mutation is in flight. */
  isStarting: boolean;
  /** Invoked when the user confirms. */
  onConfirm: (idempotencyKey: string) => void;
  /** Invoked when the user cancels. */
  onCancel: () => void;
}

const STEPS = [
  {
    name: "export_data",
    label: "Export tenant data (schema + storage manifest)",
  },
  { name: "delete_realm", label: "Delete Keycloak realm" },
  { name: "drop_schema", label: "Drop PostgreSQL schema (CASCADE)" },
  {
    name: "delete_storage_prefix",
    label: "Delete all objects under storage prefix",
  },
  { name: "unregister_subdomain", label: "Unregister tenant subdomain" },
];

function generateIdempotencyKey(tenantSlug: string): string {
  // Crypto-safe UUID with the slug as a namespace prefix. The server
  // uses the full string as the saga idempotency key; the slug prefix
  // makes the key human-readable in logs.
  const rnd = (): string => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  };
  return `deprovision:${tenantSlug}:${rnd()}`;
}

export function DeprovisionDialog({
  tenantSlug,
  tenantName,
  exportPath,
  isStarting,
  onConfirm,
  onCancel,
}: DeprovisionDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [typedSlug, setTypedSlug] = React.useState("");
  const [acknowledgedPermanent, setAcknowledgedPermanent] =
    React.useState(false);

  const slugMatches = typedSlug === tenantSlug;
  const canConfirm = slugMatches && acknowledgedPermanent && !isStarting;

  const handleConfirm = (): void => {
    if (!canConfirm) return;
    onConfirm(generateIdempotencyKey(tenantSlug));
  };

  // Disable Escape while the start mutation is in flight to prevent
  // losing the in-flight request.
  React.useEffect(() => {
    if (!isStarting) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isStarting]);

  // FR-UI-CORE-9: focus trap on the active dialog. Autofocus the
  // Cancel button (safer default than the destructive Confirm
  // button). The trap activates on mount; cleanup restores focus
  // to the previously focused element.
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return undefined;
    const cancelButton = el.querySelector<HTMLButtonElement>(
      'button[data-testid="deprovision-cancel"]',
    );
    return focusTrap(el, { initialFocus: cancelButton ?? null });
  }, []);

  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="deprov-title"
      aria-describedby="deprov-warning"
      className="deprovision-dialog"
    >
      <h2 id="deprov-title">
        De-provision tenant {tenantName ? `“${tenantName}”` : ""}
      </h2>

      <p id="deprov-warning" className="deprovision-warning">
        <strong>This action is permanent and irreversible.</strong> The export
        written to the platform export path is the <em>only</em> recovery
        artifact. There is no automated reversal.
      </p>

      <section>
        <h3>What will be deleted (in order)</h3>
        <ol>
          {STEPS.map((s) => (
            <li key={s.name}>
              <code>{s.name}</code> — {s.label}
            </li>
          ))}
        </ol>
      </section>

      {exportPath ? (
        <section>
          <h3>Export destination</h3>
          <p>
            Schema dump and object-storage manifest will be written under
            <code>
              {exportPath}/{tenantSlug}/
            </code>
            .
          </p>
        </section>
      ) : null}

      <section className="deprovision-gate">
        <label htmlFor="typed-slug">
          Type the tenant slug <code>{tenantSlug}</code> to confirm:
        </label>
        <input
          id="typed-slug"
          data-testid="deprovision-slug-input"
          type="text"
          value={typedSlug}
          onChange={(e) => setTypedSlug(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />

        <label className="deprovision-checkbox-label">
          <input
            type="checkbox"
            data-testid="deprovision-permanent-checkbox"
            checked={acknowledgedPermanent}
            onChange={(e) => setAcknowledgedPermanent(e.target.checked)}
          />
          I understand this action is permanent and cannot be undone.
        </label>
      </section>

      <footer className="deprovision-dialog-footer">
        <button
          type="button"
          onClick={onCancel}
          disabled={isStarting}
          data-testid="deprovision-cancel"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          data-testid="deprovision-confirm"
          aria-disabled={!canConfirm}
        >
          {isStarting ? t("common.loading") : t("deprovision.confirm")}
        </button>
      </footer>
    </div>
  );
}
